import React, { useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import { ICostItem } from "../InvoiceFormCostTypes";
import "../invoiceFormCosts.scss";

interface AddQuoteItemDialogProps {
  show: boolean;
  close: () => void;
  costItems: ICostItem[];
  append: (item: any) => void;
  title: string;
  data: any;
}

function AddQuoteItemDialog({ show, close, costItems, append, title, data }: AddQuoteItemDialogProps) {
  const intl = useIntl();

  const [addedIndexes, setAddedIndexes] = useState<number[]>([]);
  const [selectedItems, setSelectedItems] = useState<ICostItem[]>([]);

  const isExistingItem = (itemId: number) => data.some((item: any) => item.quoted_cost_id === itemId);
  const isItemAdded = (itemId: number) => addedIndexes.includes(itemId);

  const formatTitle = () => {
    let formattedTitle;
    if (title === "main_freight") {
      formattedTitle = "Freight";
    } else formattedTitle = title;
    return formattedTitle;
  };

  const handleRemoveItem = (item: ICostItem, index: number) => {
    setAddedIndexes(addedIndexes.filter((i) => i !== index));
    setSelectedItems(selectedItems.filter((i) => i.description !== item.description));
  };

  const handleAddItem = (item: ICostItem, index: number) => {
    // Add the index of the added item to the state (using index as item has no unique identifier)
    setAddedIndexes([...addedIndexes, index]);
    setSelectedItems([...selectedItems, item]);
  };

  const handleSave = () => {
    selectedItems.forEach((item) => {
      append({
        charge_name: item.description,
        quoted_amount: item.amount,
        type: 1,
        invoiced_cost: item.amount,
        invoice_cost_currency_code: item.currency_code,
        quoted_cost_id: item.id,
      });
    });
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setAddedIndexes([]);
    setSelectedItems([]);
    close();
  };

  return (
    <Dialog
      size="md"
      show={show}
      onHide={handleCloseDialog}
      title={intl.formatMessage({
        id: "invoices.costs.addQuoteItemDialog",
        defaultMessage: `Add item from quote`,
      })}
    >
      <div>
        {costItems.length > 0 && (
          <table className="additional-item-table mb-3">
            <thead>
              <tr>
                <th>
                  <FormattedMessage id="quote-request.charges" defaultMessage="Charges" />
                </th>

                <th>
                  <FormattedMessage id="quotation-form.appliesTo" defaultMessage="Applies to" />
                </th>
                <th>
                  <FormattedMessage id="quotation-form.totalCost" defaultMessage="Total cost" />
                </th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {costItems.map((item, i) => (
                <tr
                  key={i}
                  style={{
                    height: "50px",
                    borderBottom: i === costItems.length - 1 ? "none" : "1px solid #e9ecee",
                  }}
                >
                  <td>{item.description}</td>
                  <td>{formatTitle()}</td>
                  <td>{item.amount}</td>
                  <td>
                    {item.id && isExistingItem(item.id) && (
                      <Button className="ml-auto" disabled>
                        <FormattedMessage id="general.add" defaultMessage="Add" />
                      </Button>
                    )}
                    {item.id && !isExistingItem(item.id) && (
                      <>
                        {isItemAdded(i) ? (
                          <Button className="ml-auto" onClick={() => handleRemoveItem(item, i)}>
                            <FormattedMessage id="report.remove" defaultMessage="Remove" />
                          </Button>
                        ) : (
                          <Button className="ml-auto" onClick={() => handleAddItem(item, i)}>
                            <FormattedMessage id="general.add" defaultMessage="Add" />
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="d-flex justify-content-between mt-5">
          <Button variant="outline-primary" onClick={handleCloseDialog}>
            <FormattedMessage id="settings.provider.close" defaultMessage="Close" />
          </Button>
          <Button onClick={handleSave}>
            <FormattedMessage id="general.save" defaultMessage="Save" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export default AddQuoteItemDialog;
